import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { RecordFile, RecordManagerContext, RecordOptions, RecordType } from '../../shared/record/types';

@Injectable()
export class RecordManagerService {
	public readonly activeRecord = computed(() => {
		const activeId = this._activeRecordId();
		return this._openRecords().find((record) => record.id === activeId);
	});
	public readonly hasUnsavedChanges = computed(() => this._openRecords().some((record) => record.isDirty === true));

	private readonly STORAGE_KEY = 'klifd-record-manager-records';
	private readonly ACTIVE_RECORD_KEY = 'klifd-record-manager-active';

	private currentContext: RecordManagerContext = 'default';
	private representedUserId?: number;

	private get recordsStorageKey() {
		if (this.currentContext === 'representation') {
			return `klifd-record-manager-records-representation-${this.representedUserId}`;
		}

		return this.STORAGE_KEY;
	}

	private get activeRecordStorageKey() {
		if (this.currentContext === 'representation') {
			return `klifd-record-manager-active-representation-${this.representedUserId}`;
		}

		return this.ACTIVE_RECORD_KEY;
	}

	private readonly _openRecords = signal<RecordFile[]>(this.loadRecordsFromStorage());
	private readonly _activeRecordId = signal<string | undefined>(this.loadActiveRecordFromStorage());

	private readonly router = inject(Router);

	public readonly openRecords = this._openRecords.asReadonly();

	// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
	private readonly routeMapping: {
		[key in RecordType]: { path: string; useQuery: boolean; defaultIcon: string; defaultTitle: string };
	} = {
		[RecordType.CLIENT]: {
			path: '/desk/client/:id',
			useQuery: false,
			defaultIcon: 'person',
			defaultTitle: 'Klient',
		},
		[RecordType.CREATE_CLIENT]: {
			path: '/desk/client/add',
			useQuery: true,
			defaultIcon: 'person_add',
			defaultTitle: 'Neu: Klient',
		},
		[RecordType.INSTITUTION]: {
			path: '/desk/institution/:id',
			useQuery: false,
			defaultIcon: 'business',
			defaultTitle: 'Institution',
		},
		[RecordType.INTEGRATION_OFFICE_SERVICE]: {
			path: '/desk/integration-service-office/:id',
			useQuery: false,
			defaultIcon: 'apartment',
			defaultTitle: 'Integrationsfachdienst',
		},
		[RecordType.SPONSOR]: {
			path: '/desk/sponsor/:id',
			useQuery: false,
			defaultIcon: 'account_balance',
			defaultTitle: 'Auftraggeber',
		},
		[RecordType.SPONSOR_PERSONS]: {
			path: '/desk/sponsor/:id/persons',
			useQuery: false,
			defaultIcon: 'book',
			defaultTitle: 'Ansprechpartner',
		},
		[RecordType.INSTITUTION_PERSONS]: {
			path: '/desk/institution/:id/person',
			useQuery: false,
			defaultIcon: 'book',
			defaultTitle: 'Ansprechpartner',
		},
		[RecordType.INSTITUTION_WORKPLACE]: {
			path: '/desk/institution/:id/workplace',
			useQuery: false,
			defaultIcon: 'business_center',
			defaultTitle: 'Zweigstelle',
		},
		[RecordType.CREATE_INSTITUTION]: {
			path: '/desk/institution/add',
			useQuery: true,
			defaultIcon: 'add_business',
			defaultTitle: 'Neu: Institution',
		},
		[RecordType.CREATE_INSTITUTION_PERSON]: {
			path: '/desk/institution/:id/person/add',
			useQuery: true,
			defaultIcon: 'book',
			defaultTitle: 'Neu: Ansprechpartner',
		},
		[RecordType.CREATE_SPONSOR_PERSON]: {
			path: '/desk/sponsor/:id/persons/add',
			useQuery: false,
			defaultIcon: 'book',
			defaultTitle: 'Neu: Ansprechpartner',
		},
		[RecordType.CREATE_INSTITUTION_WORKPLACE]: {
			path: '/desk/institution/:id/workplace/add',
			useQuery: false,
			defaultIcon: 'business_center',
			defaultTitle: 'Neu: Zweigstelle',
		},
		[RecordType.CREATE_USER]: {
			path: '/desk/user/add',
			useQuery: true,
			defaultIcon: 'add_person',
			defaultTitle: 'Neu: Anwender',
		},
		[RecordType.CREATE_NETWORK_CONTACT]: {
			path: '/desk/network-contact/add',
			useQuery: true,
			defaultIcon: 'connect_without_contact',
			defaultTitle: 'Neu: Netzwerkkontakt',
		},
		[RecordType.NETWORK_CONTACT]: {
			path: '/desk/network-contact/:id',
			useQuery: false,
			defaultIcon: 'connect_without_contact',
			defaultTitle: 'Netzwerkkontakt',
		},
		[RecordType.USER]: {
			path: '/desk/user/:id',
			useQuery: false,
			defaultIcon: 'assignment',
			defaultTitle: 'Fachkraft',
		},
		[RecordType.SPECIALIST_REPRESENTATION]: {
			path: '/desk/user/:id/representation',
			useQuery: false,
			defaultIcon: 'groups',
			defaultTitle: 'Vertretung',
		},
	};

	public openRecord(type: RecordType, options: RecordOptions = {}) {
		const existingRecord = this.findExistingRecord(type, options);

		if (existingRecord && !options.forceNew) {
			if (options.route) {
				this.updateRecordRoute(existingRecord, options.route);
			}
			this.switchToRecord(existingRecord.id);
			return;
		}

		const newRecord = this.createRecord(type, options);
		this.addRecord(newRecord);
		this.switchToRecord(newRecord.id);

		return newRecord;
	}

	/**
	 * Closes all open records and resets the active record.
	 * This method clears the current session storage and updates the state.
	 */
	public closeAllRecords() {
		this._openRecords.set([]);
		this._activeRecordId.set(undefined);
		this.saveToStorage();
	}

	public closeRecord(recordId: string) {
		const records = this._openRecords();
		const recordIndex = records.findIndex((r) => r.id === recordId);

		if (recordIndex === -1) return;

		const updatedRecords = records.filter((r) => r.id !== recordId);
		this._openRecords.set([...updatedRecords]);

		// If this was the active record, switch to another one
		if (this._activeRecordId() === recordId) {
			const newActiveRecord = updatedRecords.length > 0 ? updatedRecords[updatedRecords.length - 1] : null;
			this._activeRecordId.set(newActiveRecord?.id);
		}

		this.saveToStorage();
	}

	public updateRecordTitleOfActiveRecord(newTitle: string) {
		const record = this.activeRecord();
		if (!record) return;

		record.title = newTitle;

		const updatedRecords = this._openRecords().map((r) => (r.id === record.id ? record : r));
		this._openRecords.set(updatedRecords);
		this.saveToStorage();
	}

	public switchToRecord(recordId: string) {
		const record = this._openRecords().find((r) => r.id === recordId);
		if (!record) return;

		// Update last accessed time
		record.lastAccessed = new Date();
		this._activeRecordId.set(recordId);
		this.saveToStorage();
	}

	public closeAndSwitchToNextRecord() {
		const closingRecordId = this._activeRecordId();

		if (!closingRecordId) {
			this.router.navigateByUrl('/desk');
			return;
		}

		const records = this._openRecords();
		const closingRecordIndex = records.findIndex((record) => record.id === closingRecordId);

		if (closingRecordIndex === -1) {
			this._activeRecordId.set(undefined);
			this.saveToStorage();
			this.router.navigateByUrl('/desk');
			return;
		}

		const updatedRecords = records.filter((record) => record.id !== closingRecordId);

		this._openRecords.set(updatedRecords);

		if (updatedRecords.length === 0) {
			this._activeRecordId.set(undefined);
			this.saveToStorage();
			this.router.navigateByUrl('/desk');
			return;
		}

		const nextIndex = closingRecordIndex >= updatedRecords.length ? updatedRecords.length - 1 : closingRecordIndex;
		const nextRecord = updatedRecords[nextIndex];

		this._activeRecordId.set(nextRecord.id);
		this.saveToStorage();

		this.router.navigateByUrl(nextRecord.route);
	}

	public getOpenRecords = this.openRecords;
	public getActiveRecord = this.activeRecord;

	public generateTemporaryId(): string {
		return crypto.randomUUID();
	}

	public markRecordDirty(recordId: string, isDirty: boolean) {
		const records = this._openRecords();
		const record = records.find((r) => r.id === recordId);
		if (record) {
			record.isDirty = isDirty;
			this._openRecords.set([...records]);
			this.saveToStorage();
		}
	}

	public switchToRepresentationContext(representedUserId: number) {
		this.saveToStorage();

		this.currentContext = 'representation';
		this.representedUserId = representedUserId;

		this.reloadFromStorage();
	}

	public switchToDefaultContext() {
		this.saveToStorage();

		this.currentContext = 'default';
		this.representedUserId = undefined;

		this.reloadFromStorage();
	}

	// Method to easily switch from sessionStorage to localStorage
	public switchStorageType(useLocalStorage: boolean) {
		const currentRecords = this._openRecords();
		const currentActive = this._activeRecordId();

		// Clear current storage
		sessionStorage.removeItem(this.STORAGE_KEY);
		sessionStorage.removeItem(this.ACTIVE_RECORD_KEY);
		localStorage.removeItem(this.STORAGE_KEY);
		localStorage.removeItem(this.ACTIVE_RECORD_KEY);

		// Save to new storage type
		const storage = useLocalStorage ? localStorage : sessionStorage;
		try {
			storage.setItem(this.STORAGE_KEY, JSON.stringify(currentRecords));
			storage.setItem(this.ACTIVE_RECORD_KEY, currentActive || '');
		} catch (error) {
			console.error('Failed to switch storage type:', error);
		}
	}

	private findExistingRecord(type: RecordType, options: RecordOptions) {
		return this._openRecords().find((record) => {
			if (record.type !== type) return false;

			// For existing entities, match by entityId
			if (options.entityId && !record.isTemporary) {
				return record.entityId === options.entityId;
			}

			if (!options.entityId && record.isTemporary) {
				return true; // Temporary records are always unique unless explicitly matched
			}

			// For temporary records, each one is unique unless explicitly matched
			return false;
		});
	}

	private createRecord(type: RecordType, options: RecordOptions) {
		const mapping = this.routeMapping[type];
		const isTemporary = !options.entityId || options.forceNew || false;
		const recordId = this.generateTemporaryId();

		const route = options.route ?? this.buildRoute(mapping.path, recordId, mapping.useQuery, options.entityId);

		const now = new Date();
		const record: RecordFile = {
			id: recordId,
			type,
			title: options.title || mapping.defaultTitle,
			route,
			icon: options.icon || mapping.defaultIcon,
			isTemporary,
			createdAt: now,
			entityId: options.entityId,
			lastAccessed: now,
			metadata: options.metadata || {},
		};
		return record;
	}

	private updateRecordRoute(record: RecordFile, newRoute: string) {
		const records = this._openRecords();
		const updatedRecords = records.map((r) => (r.id === record.id ? { ...r, route: newRoute } : r));
		this._openRecords.set(updatedRecords);
		this.saveToStorage();
	}

	private buildRoute(pathTemplate: string, id: string, useQuery: boolean, entityId?: string) {
		if (useQuery) {
			return `${pathTemplate}?tempId=${id}`;
		}
		if (entityId) {
			return pathTemplate.replace(':id', entityId);
		}
		return pathTemplate.replace(':id', id);
	}

	private addRecord(record: RecordFile) {
		const records = this._openRecords();
		this._openRecords.set([...records, record]);
		this.saveToStorage();
	}

	private reloadFromStorage() {
		this._openRecords.set(this.loadRecordsFromStorage());
		this._activeRecordId.set(this.loadActiveRecordFromStorage());
	}

	private loadRecordsFromStorage() {
		try {
			const stored = sessionStorage.getItem(this.recordsStorageKey);
			if (!stored) return [];

			const parsed = JSON.parse(stored);
			// Convert date strings back to Date objects
			return parsed.map((record: RecordFile) => ({
				...record,
				createdAt: new Date(record.createdAt),
				lastAccessed: new Date(record.lastAccessed),
			}));
		} catch {
			return [];
		}
	}

	private loadActiveRecordFromStorage() {
		try {
			return sessionStorage.getItem(this.activeRecordStorageKey) || undefined;
		} catch {
			return undefined;
		}
	}

	private saveToStorage() {
		try {
			sessionStorage.setItem(this.recordsStorageKey, JSON.stringify(this._openRecords()));
			sessionStorage.setItem(this.activeRecordStorageKey, this._activeRecordId() || '');
		} catch (error) {
			console.error('Failed to save records to storage:', error);
		}
	}
}
